/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable @next/next/no-img-element */

'use client';

import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { IcCloseCircle, IconButton } from '@musinsa/mds';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { BaseProductImageItem } from '../product/components/variants/BaseProductImageItem';

interface SortableItemProps<T extends BaseProductImageItem> {
  id: string;
  item: T;
  onUnselect: () => void;
}

const SortableItem = <T extends BaseProductImageItem>({
  id,
  item,
  onUnselect,
}: SortableItemProps<T>) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  const [hasImgError, setHasImgError] = useState(false);
  const { service } = useDeviceInfo();

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="inline-flex mr-1">
      <div
        className={clsx(
          'relative',
          service === SERVICE.MUSINSA ? 'w-[48px] h-[56px]' : 'w-[48px] h-[48px]',
        )}
        role="button"
        tabIndex={0}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          onUnselect();
        }}
      >
        <div
          {...attributes}
          {...listeners}
          className={clsx(
            'drag-handlecursor-grab active:cursor-grabbing absolute bottom-0 left-0 rounded overflow-hidden bg-[#e5e5e5]',
            service === SERVICE.MUSINSA ? 'w-[40px] h-[48px]' : 'w-[40px] h-[40px]',
          )}
        >
          {/* mds Image 사용 시, 하단 여백으로 인한 스크롤 발생 */}
          <img
            src={item.imageUrl}
            alt={item.goodsName || `상품 ${item.goodsNo}`}
            className={clsx('w-full h-full pointer-events-none', hasImgError && 'invisible')}
            loading="lazy"
            decoding="async"
            width={service === SERVICE.MUSINSA ? 40 : 40}
            height={service === SERVICE.MUSINSA ? 48 : 40}
            onError={() => setHasImgError(true)}
          />
        </div>
        <IconButton
          size="size20x20"
          className="absolute top-0 right-0 z-[100]"
          style={{ pointerEvents: 'auto' }}
        >
          <IcCloseCircle />
        </IconButton>
      </div>
    </div>
  );
};

interface Props<T extends BaseProductImageItem> {
  selectedProducts: T[];
  onUnselect: (goodsNo: string) => void;
  onDragEnd?: (items: T[]) => void;
}

export const DraggableSelectedProductList = <T extends BaseProductImageItem>({
  selectedProducts,
  onUnselect,
  onDragEnd,
}: Props<T>) => {
  const [dragItems, setDragItems] = useState<T[]>([]);
  const { isTouchDevice } = useDeviceInfo();

  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: {
      distance: 8, // 8px 이동해야 드래그 시작
    },
  });

  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 300, // 250ms 길게 눌러야 드래그 시작
      tolerance: 5, // 5px 이내 움직임은 허용
    },
  });

  const sensors = useSensors(isTouchDevice ? touchSensor : pointerSensor);

  // 초기 데이터 설정
  useEffect(() => {
    if (!selectedProducts.length) {
      setDragItems([]);
      return;
    }
    setDragItems(selectedProducts);
  }, [selectedProducts]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over || !dragItems.length) {
        return;
      }

      if (active.id !== over.id) {
        const oldIndex = dragItems.findIndex((item) => String(item.goodsNo) === String(active.id));
        const newIndex = dragItems.findIndex((item) => String(item.goodsNo) === String(over.id));

        const newItems = arrayMove(dragItems, oldIndex, newIndex);
        setDragItems(newItems);
        onDragEnd?.(newItems);
      }
    },
    [dragItems, onDragEnd],
  );

  const handleUnselect = (goodsNo: string | number) => {
    onUnselect(String(goodsNo));
  };

  const dragItemIds = useMemo(() => dragItems.map((item) => String(item.goodsNo)), [dragItems]);

  if (selectedProducts.length === 0) {
    return null;
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToHorizontalAxis]} // Drag를 수평으로 제한
      autoScroll={{ threshold: { x: 0.2, y: 0 } }}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={dragItemIds} strategy={horizontalListSortingStrategy}>
        <div className="py-1">
          <div className="flex gap-x-[4px] overflow-x-auto scrollbar-hide px-4">
            {dragItems.map((item) => (
              <SortableItem
                key={String(item.goodsNo)}
                id={String(item.goodsNo)}
                item={item}
                onUnselect={() => handleUnselect(item.goodsNo)}
              />
            ))}
          </div>
        </div>
      </SortableContext>
    </DndContext>
  );
};
